// TruthCard component

import type { Truth, FeedItem, FeedItemReply } from '../api/types';
import { navigate } from '../router';
import { icon } from '../icons';

function formatTime(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
}

function formatFullDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString();
}

interface TimeDisplay {
  text: string;
  tooltip: string | null;
}

function getTimeDisplay(createdAt: string | null, bumpedAt: string | null): TimeDisplay {
  if (bumpedAt) {
    return {
      text: `bumped ${formatTime(bumpedAt)}`,
      tooltip: `Originally posted: ${formatFullDate(createdAt)}`,
    };
  }
  return {
    text: formatTime(createdAt),
    tooltip: null,
  };
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function linkifyContent(content: string): string {
  // Escape HTML first
  const escaped = escapeHtml(content);
  // Convert URLs to links
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  return escaped.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener">$1</a>');
}

export function createTruthCard(truth: Truth): HTMLElement {
  const card = document.createElement('article');
  card.className = 'truth-card';
  card.dataset.truthId = truth.id;

  const authorName = truth.author?.name || 'Unknown';
  const avatarUrl = truth.author?.avatar_url;
  const avatarHtml = avatarUrl
    ? `<img class="truth-card__avatar" src="${escapeHtml(avatarUrl)}" alt="${escapeHtml(authorName)}">`
    : '';

  let quotedHtml = '';
  if (truth.quoted_truth) {
    quotedHtml = `
      <div class="truth-card__quoted">
        <div class="truth-card__quoted-author">@${escapeHtml(truth.quoted_truth.author.name)}</div>
        <div>${escapeHtml(truth.quoted_truth.content)}</div>
      </div>
    `;
  }

  // Display stats as read-only with SVG icons
  const statsHtml = `
    <div class="truth-card__stats">
      <span class="truth-card__stat truth-card__stat--likes">${icon('heart', 12)} ${truth.likes}</span>
      <span class="truth-card__stat truth-card__stat--reposts">${icon('repost', 12)} ${truth.retruths}</span>
      <span class="truth-card__stat truth-card__stat--comments">${icon('comment', 12)} ${truth.replies}</span>
    </div>
  `;

  const timeDisplay = getTimeDisplay(truth.created_at, truth.bumped_at);
  const timeHtml = timeDisplay.tooltip
    ? `<span class="truth-card__time" title="${escapeHtml(timeDisplay.tooltip)}">${escapeHtml(timeDisplay.text)}</span>`
    : `<span class="truth-card__time">${escapeHtml(timeDisplay.text)}</span>`;

  card.innerHTML = `
    <div class="truth-card__header">
      <a href="#/@${encodeURIComponent(authorName)}" class="truth-card__author">
        ${avatarHtml}
        @${escapeHtml(authorName)}
      </a>
      ${timeHtml}
    </div>
    <div class="truth-card__content">${linkifyContent(truth.content)}</div>
    ${quotedHtml}
    ${statsHtml}
  `;

  // Click on card goes to truth detail (but not on links)
  card.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('a')) {
      return;
    }
    navigate(`/truth/${truth.id}`);
  });

  return card;
}

export function createFeedItemCard(item: FeedItem): HTMLElement {
  if (item.type === 'reply') {
    return createReplyCard(item);
  }
  return createTruthCard(item);
}

function createReplyCard(reply: FeedItemReply): HTMLElement {
  const card = document.createElement('article');
  card.className = 'truth-card truth-card--reply';
  card.dataset.truthId = reply.id;  // Use reply's own ID for feed deduplication
  card.dataset.parentTruthId = reply.truth_id;

  const authorName = reply.author?.name || 'Unknown';
  const avatarUrl = reply.author?.avatar_url;
  const avatarHtml = avatarUrl
    ? `<img class="truth-card__avatar" src="${escapeHtml(avatarUrl)}" alt="${escapeHtml(authorName)}">`
    : '';

  // Parent truth context
  let parentHtml = '';
  if (reply.parent_truth) {
    const parentAuthor = reply.parent_truth.author?.name || 'Unknown';
    const truncatedContent = reply.parent_truth.content.length > 100
      ? reply.parent_truth.content.substring(0, 100) + '...'
      : reply.parent_truth.content;
    parentHtml = `
      <div class="truth-card__parent">
        <span class="truth-card__parent-label">${icon('reply', 12)} replying to @${escapeHtml(parentAuthor)}</span>
        <div class="truth-card__parent-content">${escapeHtml(truncatedContent)}</div>
      </div>
    `;
  }

  // Display stats - replies only have likes
  const statsHtml = `
    <div class="truth-card__stats">
      <span class="truth-card__stat truth-card__stat--likes">${icon('heart', 12)} ${reply.likes}</span>
    </div>
  `;

  const timeDisplay = getTimeDisplay(reply.created_at, reply.bumped_at);
  const timeHtml = timeDisplay.tooltip
    ? `<span class="truth-card__time" title="${escapeHtml(timeDisplay.tooltip)}">${escapeHtml(timeDisplay.text)}</span>`
    : `<span class="truth-card__time">${escapeHtml(timeDisplay.text)}</span>`;

  card.innerHTML = `
    ${parentHtml}
    <div class="truth-card__header">
      <a href="#/@${encodeURIComponent(authorName)}" class="truth-card__author">
        ${avatarHtml}
        @${escapeHtml(authorName)}
      </a>
      ${timeHtml}
    </div>
    <div class="truth-card__content">${linkifyContent(reply.content)}</div>
    ${statsHtml}
  `;

  // Click on card goes to parent truth detail (but not on links)
  card.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('a')) {
      return;
    }
    navigate(`/truth/${reply.truth_id}`);
  });

  return card;
}
